import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    View,
    GridColumn,
    Pagination,
    colors,
    useViewport,
} from '@fairygodboss/ui';
import { JobsSearchResultsHeader } from './JobsSearchResultsHeader';
import { MemoizedJobCard } from './JobSearchCard';

export const calculateIsActivelyHiring = (jobCount) => {
    if (typeof jobCount !== 'number') return false;
    return jobCount >= 100;
};

const JobResultList = ({
    onSaveJob,
    jobsList,
    isSM,
    activeJob,
    activeListingRef,
    onJobSelect,
    searchQueryId,
    searchIndexName,
    activePage,
    pageLimit,
    singleJobDetail,
}) => {
    return useMemo(
        () =>
            jobsList?.map((job, i) => {
                const chips = [];
                if (job?.isSponsored) chips.push('sponsored');
                if (job.isRemote) chips.push('remote');
                if (calculateIsActivelyHiring(job?.company?.jobCount))
                    chips.push('hiring');

                const _position = (activePage - 1) * pageLimit + i + 1;
                const searchTracking = {
                    positionInListing: _position,
                    queryID: searchQueryId,
                    indexName: searchIndexName,
                };

                const ListComponent = () => (
                    <MemoizedJobCard
                        position={_position}
                        onSaveJob={onSaveJob}
                        key={`job-result-${job.hash}-${_position}`}
                        job={job}
                        chips={chips}
                        isSM={isSM}
                        activeJob={activeJob}
                        activeListingRef={activeListingRef}
                        onJobSelect={onJobSelect}
                        searchTracking={searchTracking}
                    />
                );
                return (
                    <ListComponent
                        key={`job-result-${job.hash}-${_position}`}
                    />
                );
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [jobsList, activeJob, isSM, singleJobDetail]
    );
};

export const JobsSearchResults = ({
    disableAlertToggle,
    onJobAlertSettingClick,
    alertToggleValue,
    onAlterToggleClick,
    totalJobs,
    totalJobAlerts,
    jobSearchQuery,
    activePage,
    onPageSelect,
    onJobSelect,
    jobsList,
    activeJob,
    isAuthenticatedUser,
    jobSearchLocation,
    onSaveJob,
    searchQueryId,
    searchIndexName,
    pageLimit,
    singleJobDetail,
}) => {
    const totalPages = Math.ceil(totalJobs / 20);
    const scrollContainerRef = useRef();
    const activeListingRef = useRef();
    const { isSM } = useViewport();

    useEffect(() => {
        if (!isSM) {
            window.scrollTo(0, 0);
        }
    }, [isSM, activePage]);

    useEffect(() => {
        if (activeListingRef?.current && isSM) {
            requestAnimationFrame(() => {
                activeListingRef.current?.scrollIntoView({ block: 'end' });
            });
            return;
        }
        if (isSM) {
            scrollContainerRef.current?.scrollIntoView();
        }
    }, [activePage, isSM, jobsList]);

    const [page, setPage] = useState(activePage);
    useEffect(() => {
        if (jobsList && jobsList[0]?.userLiked !== undefined) {
            setPage(activePage);
        }
    }, [jobsList, activePage]);
    return (
        <GridColumn
            borderRightColor={isSM && colors.secondary[20]}
            borderRightWidth="1px"
            borderRightStyle={isSM ? 'solid' : 'none'}
            size={isSM ? 5 : 12}
            height="100%"
            display="flex"
            flexDirection="column"
            overflowY="auto"
            overflowX="hidden"
            position="relative"
            zIndex="1"
        >
            <View>
                <div ref={scrollContainerRef} />
                <JobsSearchResultsHeader
                    disableAlertToggle={disableAlertToggle}
                    jobSearchQuery={jobSearchQuery}
                    jobSearchLocation={jobSearchLocation}
                    totalJobs={totalJobs}
                    onJobAlertSettingClick={onJobAlertSettingClick}
                    alertToggleValue={alertToggleValue}
                    onAlterToggleClick={onAlterToggleClick}
                    totalJobAlerts={totalJobAlerts}
                    isAuthenticatedUser={isAuthenticatedUser}
                />
                <JobResultList
                    pageLimit={pageLimit}
                    onSaveJob={onSaveJob}
                    jobsList={jobsList}
                    isSM={isSM}
                    activeJob={activeJob}
                    activeListingRef={activeListingRef}
                    onJobSelect={onJobSelect}
                    searchQueryId={searchQueryId}
                    searchIndexName={searchIndexName}
                    activePage={page}
                    singleJobDetail={singleJobDetail}
                />
                <View
                    height="94px"
                    display="grid"
                    justifyContent="center"
                    alignItems="center"
                >
                    {totalPages ? (
                        <Pagination
                            menuPlacement="top"
                            onChange={(pageNum) => {
                                activeListingRef.current = null;
                                onPageSelect(pageNum);
                            }}
                            page={activePage}
                            total={totalPages}
                        />
                    ) : null}
                </View>
            </View>
        </GridColumn>
    );
};
